import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Pause, Play, Power } from "lucide-react";
import { toast } from "sonner";
import { api, euro, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { SUB_STATUS, frDate } from "@/lib/subscription";
import { SubPicker } from "@/components/subscription/SubPicker";
import { CancelReasons } from "@/components/subscription/CancelReasons";
import { IntervalChooser, ReassortPicker, reassortTotals } from "@/components/subscription/ReassortPicker";

const btn = "inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 text-xs font-semibold uppercase tracking-[0.18em] transition-colors disabled:opacity-40";

function ReassortEditor({ sub, busy, onSave }) {
  const [items, setItems] = useState((sub.items || []).map((i) => ({ slug: i.slug, quantity: i.quantity })));
  const [interval, setFreq] = useState(sub.interval || 1);
  const key = (list, n) => `${n}|${list.map((i) => `${i.slug}:${i.quantity}`).join()}`;
  const changed = key(items, interval) !== key(sub.items || [], sub.interval || 1);
  const { total } = reassortTotals(sub.choices, items);
  return (
    <section data-testid="reassort-editor" className="space-y-6">
      <div>
        <p className="eyebrow mb-2">Mes produits à -10 %</p>
        <p className="mb-5 text-sm text-[var(--muted)]">Modifiez vos produits ou votre rythme avant la prochaine livraison. Un changement de rythme s'applique après le prochain envoi déjà prévu, sans prélèvement immédiat.</p>
        <ReassortPicker products={sub.choices} value={items} onChange={setItems} />
      </div>
      <IntervalChooser value={interval} onChange={setFreq} />
      <button data-testid="reassort-save" disabled={!changed || !items.length || !!busy} onClick={() => onSave({ items, interval })}
        className={`${btn} bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)]`}>{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Enregistrer · {euro(total)} produits</button>
    </section>
  );
}

function LinkRequest() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/subscriptions/link", { email: email.trim() }); setSent(true); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  if (sent) return <p data-testid="sub-link-sent" className="rounded-sm border border-[var(--gold)]/50 p-5 text-sm">Si un abonnement est associé à cette adresse, vous allez recevoir un email avec votre lien personnel. Pensez à regarder vos courriers indésirables.</p>;
  return (
    <form onSubmit={send} data-testid="sub-link-form" className="max-w-md space-y-3">
      <p className="text-sm text-[var(--parchment)]/85">Indiquez l'email utilisé pour votre abonnement : nous vous envoyons un lien sécurisé pour le gérer.</p>
      <input data-testid="sub-link-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.fr" autoComplete="email"
        className="h-12 w-full rounded-sm border border-[var(--line)] bg-[var(--ink)] px-3 text-base outline-none focus:border-[var(--gold)]" />
      <button data-testid="sub-link-submit" disabled={busy} className={`${btn} bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)]`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Recevoir mon lien</button>
      <p className="text-xs text-[var(--muted)]">Pas encore abonnée ? <Link to="/abonnement" className="text-[var(--gold-soft)] underline underline-offset-4">Découvrir l'abonnement</Link></p>
    </form>
  );
}

function Manage({ token, sub, setSub }) {
  const [picks, setPicks] = useState(sub.products.map((p) => p.slug));
  const [busy, setBusy] = useState("");
  const [confirmStop, setConfirmStop] = useState(false);
  const live = sub.status !== "canceled";
  const changed = picks.join() !== sub.products.map((p) => p.slug).join();
  const reassort = sub.plan === "reassort";
  const extreme = sub.plan === "extreme";
  const call = async (key, fn, ok) => {
    setBusy(key);
    try { const { data } = await fn(); setSub(data); toast.success(ok); } catch (e) { toast.error(errMsg(e)); }
    setBusy(""); setConfirmStop(false);
  };
  return (
    <div className="space-y-10">
      <div data-testid="sub-overview" className="grid gap-4 rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6 sm:grid-cols-3">
        <div><p className="text-xs uppercase tracking-[0.18em] text-[var(--muted)]">Statut</p><p data-testid="sub-status" className="mt-1 font-display text-2xl text-[var(--gold-soft)]">{SUB_STATUS[sub.status] || sub.status}</p></div>
        <div><p className="text-xs uppercase tracking-[0.18em] text-[var(--muted)]">Montant</p><p data-testid="sub-monthly" className="mt-1 font-display text-2xl">{euro(sub.monthly_total)} {reassort && sub.interval > 1 ? "/ livraison" : "/ mois"}</p><p data-testid="sub-plan" className="text-xs text-[var(--muted)]">{sub.plan_label}</p></div>
        <div><p className="text-xs uppercase tracking-[0.18em] text-[var(--muted)]">{sub.status === "paused" ? "Envois" : "Prochain envoi"}</p><p data-testid="sub-next" className="mt-1 font-display text-2xl">{sub.status === "paused" ? "En pause" : live ? frDate(sub.next_billing_at) || "Bientôt" : "—"}</p></div>
        <p className="text-sm text-[var(--parchment)]/80 sm:col-span-3">{sub.shipping_label}{sub.relay ? ` · ${sub.relay.name}, ${sub.relay.zip} ${sub.relay.city}` : ""} · {sub.deliveries} colis envoyé{sub.deliveries > 1 ? "s" : ""}</p>
      </div>
      {live && reassort ? <ReassortEditor sub={sub} busy={busy} onSave={(b) => call("save", () => api.put(`/subscriptions/manage/${token}/reassort`, b), "Votre abonnement est mis à jour.")} /> : null}
      {live && extreme ? <p data-testid="sub-extreme-content" className="text-sm text-[var(--parchment)]/90">Chaque mois : <b>{sub.products[0]?.name || "Extrême Repouss 50 ml"}</b> · {euro(sub.price)} + {euro(sub.shipping_cost)} de livraison en point relais. Pour changer de point relais, répondez à l'un de nos emails.</p> : null}
      {live && !reassort && !extreme ? (
        <section>
          <p className="eyebrow mb-2">{sub.picks > 1 ? `Mes ${sub.picks} soins du prochain colis` : "Mon soin du prochain colis"}</p>
          <p className="mb-5 text-sm text-[var(--muted)]">Modifiez votre choix avant la date du prochain envoi. Sans changement, vous recevez {sub.picks > 1 ? "les mêmes soins" : "le même soin"}.</p>
          <SubPicker products={sub.choices} value={picks} onChange={setPicks} max={sub.picks} />
          <button data-testid="sub-save-products" disabled={!changed || picks.length !== sub.picks || !!busy} onClick={() => call("save", () => api.put(`/subscriptions/manage/${token}/products`, { products: picks }), "Votre choix est enregistré.")}
            className={`${btn} mt-6 bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)]`}>{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Enregistrer mon choix</button>
        </section>
      ) : null}
      {!live ? <p data-testid="sub-canceled-note" className="text-sm">Votre abonnement est arrêté : plus aucun prélèvement ne sera effectué. <Link to="/abonnement" className="text-[var(--gold-soft)] underline underline-offset-4">Me réabonner</Link></p> : null}
      {live ? (
        <section className="flex flex-wrap gap-3 border-t border-[var(--line)] pt-8">
          {sub.status === "paused" ? (
            <button data-testid="sub-resume" disabled={!!busy} onClick={() => call("resume", () => api.post(`/subscriptions/manage/${token}/resume`), "Abonnement réactivé.")} className={`${btn} border border-[var(--gold)] text-[var(--gold-soft)] hover:bg-[var(--gold)] hover:text-[var(--ink)]`}><Play className="h-4 w-4" />Reprendre mes envois</button>
          ) : (
            <button data-testid="sub-pause" disabled={!!busy} onClick={() => call("pause", () => api.post(`/subscriptions/manage/${token}/pause`), "Abonnement en pause : aucun prélèvement jusqu'à la reprise.")} className={`${btn} border border-[var(--line)] hover:border-[var(--gold)]`}><Pause className="h-4 w-4" />Mettre en pause</button>
          )}
          {confirmStop ? (
            <CancelReasons busy={busy} canPause={sub.status !== "paused"} onAbort={() => setConfirmStop(false)}
              onPause={() => call("pause", () => api.post(`/subscriptions/manage/${token}/pause`), "Abonnement en pause : aucun prélèvement jusqu'à la reprise.")}
              onConfirm={(b) => call("cancel", () => api.post(`/subscriptions/manage/${token}/cancel`, b), "Abonnement arrêté. Merci pour votre retour.")} />
          ) : (
            <button data-testid="sub-cancel" onClick={() => setConfirmStop(true)} className={`${btn} text-[var(--parchment)]/80 hover:text-red-300`}><Power className="h-4 w-4" />Arrêter mon abonnement</button>
          )}
        </section>
      ) : null}
    </div>
  );
}

export default function MySubscription() {
  const [params] = useSearchParams();
  const token = params.get("t");
  const [sub, setSub] = useState(null);
  const [state, setState] = useState(token ? "loading" : "ask");
  useSeo({ title: "Mon abonnement | Ozentya", noindex: true });
  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    api.get(`/subscriptions/manage/${token}`).then(({ data }) => { if (alive) { setSub(data); setState("ok"); } }).catch(() => { if (alive) setState("invalid"); });
    return () => { alive = false; };
  }, [token]);
  return (
    <main data-testid="my-subscription-page" className="mx-auto max-w-5xl px-5 pb-24 pt-40 md:px-10">
      <p className="eyebrow mb-4">Espace abonnée</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl">Mon <span className="italic gold-text">abonnement</span>{sub?.first_name ? <span className="block text-2xl text-[var(--muted)] md:text-3xl">Bonjour {sub.first_name}</span> : null}</h1>
      <div className="mt-12">
        {state === "loading" && <Loader2 data-testid="sub-loading" className="h-6 w-6 animate-spin text-[var(--gold)]" />}
        {state === "invalid" && <p data-testid="sub-invalid" className="mb-6 text-sm text-red-300">Ce lien n'est plus valable. Demandez un nouveau lien ci-dessous.</p>}
        {(state === "ask" || state === "invalid") && <LinkRequest />}
        {state === "ok" && sub ? <Manage token={token} sub={sub} setSub={setSub} /> : null}
      </div>
    </main>
  );
}
